/** biome-ignore-all lint/suspicious/noExplicitAny: expected */
import type { Schemes } from "@ext/markdown/core/Parser/Parser";
import { createToken } from "@ext/markdown/core/render/logic/Markdoc/src/tokenizer/plugins/annotations";
import type MarkdownIt from "markdown-it";
import type { RuleBlock } from "markdown-it/lib/parser_block";
import Function from "../../ast/function";
import Variable from "../../ast/variable";
import { parse, SyntaxError as TagSyntaxError } from "../../grammar/tag";
import { findTagEnd, tagWraps } from "../../utils";

export const parseProperty = (token: any) => {
	const propertyMatches = token.content?.match(/\{%\s*#([^:]+):([^}]+)\s*%\}/g);
	if (!propertyMatches) return;

	const newToken = { ...token };
	const properties = propertyMatches.map((match) => {
		const [, name, value] = match.match(/\{%\s*#([^:]+):([^}]+)\s*%\}/);
		const values = value.split(",").map((v) => v.trim());
		return {
			name: name.trim(),
			value: values,
		};
	});

	let newContent = token.content;

	propertyMatches.forEach((match) => {
		newContent = newContent.replace(match, "");
	});

	newToken.content = newContent.trim();

	if (newToken.children) {
		newToken.children = newToken.children.map((child) => {
			const newChild = { ...child };

			if (typeof child.content === "string") {
				let childContent = child.content;
				propertyMatches.forEach((match) => {
					childContent = childContent.replace(match, "");
				});
				newChild.content = childContent.trim();
			}

			if (child.attrs) newChild.attrs = [...child.attrs, ["property", properties]];
			else newChild.attrs = [["property", properties]];

			return newChild;
		});
	}

	return newToken;
};

const processInlineProperties = (tokens: any[]) => {
	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i];
		const newToken = parseProperty(token);

		if (newToken) {
			tokens.splice(i, 1, newToken);
		}
	}
};

const isPropertyTag = (c: any) => c.type === "tag" && c.meta?.tag === "property";

const findTagOpenIndex = (tokens: any[], from: number) => {
	let depth = 0;
	for (let j = from; j >= 0; j--) {
		if (tokens[j].type === "tag_close") depth++;
		if (tokens[j].type === "tag_open") {
			if (depth === 0) return j;
			depth--;
		}
	}
	return -1;
};

// A closing tag glued to the last line of a list item or a blockquote sits in a lazy continuation of the
// inner paragraph: it has to go after every block opened inside its tag, not only after the paragraph.
const findInsertAfterInnerBlocks = (tokens: any[], inlineIdx: number) => {
	const tagOpenIdx = findTagOpenIndex(tokens, inlineIdx);
	if (tagOpenIdx === -1) return tokens[inlineIdx + 1]?.type === "paragraph_close" ? inlineIdx + 2 : inlineIdx + 1;

	let openBlocks = 0;
	for (let j = tagOpenIdx + 1; j <= inlineIdx; j++) openBlocks += tokens[j].nesting ?? 0;

	let insertAt = inlineIdx + 1;
	while (openBlocks > 0 && tokens[insertAt]?.nesting === -1 && tokens[insertAt].type !== "tag_close") {
		openBlocks--;
		insertAt++;
	}
	return insertAt;
};

export const extractInlinePropertyTokens = (tokens: any[]) => {
	for (let i = tokens.length - 1; i >= 0; i--) {
		const token = tokens[i];
		if (token.type !== "inline" || !token.children) continue;
		if (!token.children.some(isPropertyTag)) continue;

		const toExtract = token.children.filter((c: any) => isPropertyTag(c) || c.type === "tag_close");

		const remaining = token.children.filter((c: any) => !isPropertyTag(c) && c.type !== "tag_close");
		const hasContent = remaining.some((c: any) => c.content?.trim());
		let insertAt: number;

		if (hasContent) {
			token.children = remaining;
			token.content = remaining.map((c: any) => c.content ?? "").join("");
			// The paragraph keeps its text; the closing tag and its properties go after it, not inside it.
			insertAt = findInsertAfterInnerBlocks(tokens, i);
		} else {
			tokens.splice(i, 1);
			if (i > 0 && tokens[i - 1]?.type === "paragraph_open") {
				tokens.splice(i - 1, 1);
				i--;
			}
			if (i < tokens.length && tokens[i]?.type === "paragraph_close") {
				tokens.splice(i, 1);
			}
			insertAt = i;
		}

		for (let k = toExtract.length - 1; k >= 0; k--) {
			toExtract[k].block = true;
			tokens.splice(insertAt, 0, toExtract[k]);
		}
	}
};

export const processBlockProperties = (tokens: any[]) => {
	extractInlinePropertyTokens(tokens);

	for (let i = tokens.length - 1; i >= 0; i--) {
		const token = tokens[i];
		if (token.type !== "tag" || token.meta?.tag !== "property") continue;

		const id = token.meta.attributes?.find((a: any) => a.name === "id")?.value;
		const value = token.meta.attributes?.find((a: any) => a.name === "value")?.value;
		if (!id) continue;

		let tagOpenIdx = -1;
		let depth = 0;
		for (let j = i - 1; j >= 0; j--) {
			if (tokens[j].type === "tag_close") depth++;
			if (tokens[j].type === "tag_open") {
				depth--;
				if (depth === 0) {
					tagOpenIdx = j;
					break;
				}
			}
		}

		if (tagOpenIdx === -1) continue;

		const tagOpen = tokens[tagOpenIdx];
		if (!tagOpen.attrs) tagOpen.attrs = {};
		if (!tagOpen.attrs.property) tagOpen.attrs.property = [];

		const values = typeof value === "string" ? value.split(",").map((v: string) => v.trim()) : value;
		tagOpen.attrs.property.unshift({ id, value: values });

		if (!tagOpen.meta) tagOpen.meta = {};
		if (!tagOpen.meta.attributes) tagOpen.meta.attributes = [];
		const existingAttr = tagOpen.meta.attributes.find((a: any) => a.name === "property");
		if (existingAttr) {
			existingAttr.value = tagOpen.attrs.property;
		} else {
			tagOpen.meta.attributes.push({ type: "attribute", name: "property", value: tagOpen.attrs.property });
		}

		tokens.splice(i, 1);
	}
};

const readLineTags = (src: string, from: number, to: number) => {
	const contents: string[] = [];
	let position = from;

	while (position < to) {
		const wrap = [tagWraps.angle, tagWraps.curly].find((w) => src.startsWith(w.open, position));
		if (!wrap) return null;

		const end = findTagEnd(src, position + wrap.open.length, wrap.close);
		if (end === undefined || end + wrap.close.length > to) return null;

		contents.push(src.slice(position + wrap.open.length, end).trim());
		position = end + wrap.close.length;
		while (position < to && (src[position] === " " || src[position] === "\t")) position++;
	}

	return contents;
};

const parseTag = (content: string) => {
	try {
		return parse(content, { Variable, Function });
	} catch (error) {
		if (!(error instanceof TagSyntaxError)) throw error;
		return null;
	}
};

/**
 * The properties of an element follow its closing tag on its line: `</tab> <property id="…" value="…" />`. The generic
 * tag rule reads one tag per line: without this rule the line would continue the block above and swallow the closing tag.
 */
const closingTagWithProperties =
	(tags: Schemes["tags"]): RuleBlock =>
	(state, startLine, _endLine, silent) => {
		const start = state.bMarks[startLine] + state.tShift[startLine];
		const contents = readLineTags(state.src, start, state.eMarks[startLine]);
		if (!contents || contents.length < 2) return false;

		const [closing, ...properties] = contents.map(parseTag);
		if (closing?.type !== "tag_close" || !tags?.[closing.meta.tag]) return false;
		if (!properties.every((property) => property?.type === "tag" && property.meta.tag === "property")) return false;
		if (silent) return true;

		for (const content of contents) {
			const token = createToken(state, content, 0, { tags });
			if (token) token.map = [startLine, startLine + 1];
		}

		state.line = startLine + 1;
		return true;
	};

function propertiesPlugin(md: MarkdownIt, options?: { tags: Schemes["tags"] }) {
	md.block.ruler.before("paragraph", "closingTagWithProperties", closingTagWithProperties(options?.tags), {
		alt: ["paragraph", "blockquote"],
	});
	md.core.ruler.push("properties", (state) => {
		processBlockProperties(state.tokens);
		processInlineProperties(state.tokens);
	});
}

export default propertiesPlugin;
